import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Save, Search, Percent, CheckCircle, AlertCircle, 
  Loader2, Info
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const GestionTaxes = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxRules, setTaxRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingRules, setEditingRules] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${api.baseUrl}/taxes/rules`);
      const data = response.data;
      setTaxRules(data.rules || []);
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (categorie, field, value) => {
    setEditingRules(prev => ({
      ...prev,
      [categorie]: {
        ...prev[categorie],
        [field]: value
      }
    }));
  };

  const saveRule = async (rule) => {
    setSaving(true);
    try {
      const editedData = editingRules[rule.categorie] || {};
      const updatedRule = {
        categorie: rule.categorie,
        tps: editedData.tps !== undefined ? parseFloat(editedData.tps) : rule.tps,
        tvq: editedData.tvq !== undefined ? parseFloat(editedData.tvq) : rule.tvq,
        actif: rule.actif,
        commentaire: editedData.commentaire || rule.commentaire
      };

      const response = await axios.post(`${api.baseUrl}/taxes/rule`, updatedRule);

      if (response.status === 200) {
        toast.success(`✅ Règle de taxes sauvegardée pour ${rule.categorie}`);
        setEditingRules(prev => {
          const newEditing = { ...prev };
          delete newEditing[rule.categorie];
          return newEditing;
        });
        loadData();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Filtrer les règles
  const filteredRules = taxRules.filter(rule =>
    rule.categorie.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Grouper par type de taxes
  const withTaxes = filteredRules.filter(r => r.tps > 0 || r.tvq > 0);
  const withoutTaxes = filteredRules.filter(r => r.tps === 0 && r.tvq === 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          Gestion des Règles de Taxes
        </h1>
        <p className="text-slate-500 mt-1">
          Définissez les taux de TPS et TVQ pour chaque catégorie de dépense
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 animate-fade-in delay-100">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Catégories</p>
                <p className="text-2xl font-bold text-slate-900">{taxRules.length}</p>
              </div>
              <Percent className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avec Taxes</p>
                <p className="text-2xl font-bold text-green-600">{withTaxes.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Sans Taxes (exonérées)</p>
                <p className="text-2xl font-bold text-amber-600">{withoutTaxes.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">💡 Comment ça fonctionne:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Définissez les taux de TPS et TVQ pour chaque catégorie de dépense</li>
                <li>Taux standard au Québec: TPS 5% + TVQ 9.975% = 14.975% total</li>
                <li>Certaines catégories sont exonérées (salaires, frais bancaires, etc.)</li>
                <li>Le système calculera automatiquement les montants HT (sans taxes) dans les détails</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recherche */}
      <Card className="animate-fade-in delay-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tableau des règles */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle>Règles de Taxes par Catégorie ({filteredRules.length})</CardTitle>
          <CardDescription>
            Modifiez les taux de TPS et TVQ pour chaque catégorie. Taux standard: TPS 5% + TVQ 9.975%
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="py-3 px-4 text-left font-semibold text-slate-600">Catégorie</th>
                  <th className="py-3 px-4 text-center font-semibold text-slate-600">TPS (%)</th>
                  <th className="py-3 px-4 text-center font-semibold text-slate-600">TVQ (%)</th>
                  <th className="py-3 px-4 text-center font-semibold text-slate-600">Total (%)</th>
                  <th className="py-3 px-4 text-left font-semibold text-slate-600">Commentaire</th>
                  <th className="py-3 px-4 text-center font-semibold text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map((rule) => {
                  const isEditing = editingRules[rule.categorie];
                  const currentTPS = isEditing?.tps !== undefined ? isEditing.tps : rule.tps;
                  const currentTVQ = isEditing?.tvq !== undefined ? isEditing.tvq : rule.tvq;
                  const totalTax = (parseFloat(currentTPS) + parseFloat(currentTVQ)) * 100;
                  
                  return (
                    <tr key={rule.categorie} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{rule.categorie}</span>
                          {(rule.tps === 0 && rule.tvq === 0) && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Exonéré
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          step="0.001"
                          value={currentTPS * 100}
                          onChange={(e) => handleEdit(rule.categorie, 'tps', parseFloat(e.target.value) / 100)}
                          className="w-24 text-center"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          step="0.001"
                          value={currentTVQ * 100}
                          onChange={(e) => handleEdit(rule.categorie, 'tvq', parseFloat(e.target.value) / 100)}
                          className="w-24 text-center"
                        />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-slate-700">
                          {totalTax.toFixed(3)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">
                        {rule.commentaire || "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isEditing && (
                          <Button
                            size="sm"
                            onClick={() => saveRule(rule)}
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Légende */}
      <Card className="bg-slate-50">
        <CardHeader>
          <CardTitle className="text-base">📋 Taux de Taxes au Québec</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">TPS (Taxe Produits et Services):</span>
            <span className="font-semibold text-slate-900">5.0%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">TVQ (Taxe Vente Québec):</span>
            <span className="font-semibold text-slate-900">9.975%</span>
          </div>
          <div className="flex items-center justify-between border-t pt-2">
            <span className="text-slate-600 font-medium">Taux combiné standard:</span>
            <span className="font-bold text-slate-900">14.975%</span>
          </div>
          <div className="mt-4 p-3 bg-amber-50 rounded border border-amber-200">
            <p className="text-xs text-amber-800">
              <strong>Note:</strong> Les catégories exonérées (salaires, frais bancaires, etc.) ont TPS et TVQ à 0%.
              Les montants HT (sans taxes) seront calculés automatiquement dans les analyses de dépenses.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionTaxes;
